import { useEffect, useState, useCallback, useRef } from 'react';
import { storeService } from '../services/storeService';
import { StoreCatalogItem, StoreCatalogFilter, StoreCategory, StoreColor } from '../models';
import { ProductCard } from '../components/ui/ProductCard';
import { Spinner } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { SearchableSelect, SelectOption } from '../components/ui/SearchableSelect';
import { sortFeaturedFirst } from '../utils/featuredCategories';
import { useInitEffect } from '../hooks/useInitEffect';
import { useCatalogFilters } from '../hooks/useCatalogFilters';
import { StoreEventType, trackStoreEvent } from '../services/trackingService';
import { useHeaderOffset } from '../hooks/useHeaderOffset';
import { GenderAccent } from '../components/catalog/GenderAccent';
import { BabySizeChips } from '../components/catalog/BabySizeChips';
import { AgeBucketChips } from '../components/catalog/AgeBucketChips';
import { CatalogSheet } from '../components/catalog/CatalogSheet';
import {
  ActiveFilters,
  CatalogFilterPanel,
  CategoryStrip,
  CategorySuggestions,
  PRICE_MAX_FALLBACK,
  categoryIconSrc,
  genderLabel,
} from '../components/catalog/CatalogFilterPanel';

const PAGE_SIZE = 24;
const PRICE_STEP = 10;

interface SortOption {
  label: string;
  sortBy?: string;
  sortDirection?: string;
}

const SORT_OPTIONS: SortOption[] = [
  { label: 'Destaques', sortBy: 'featured' },
  { label: 'Novidades', sortBy: 'arrival', sortDirection: 'desc' },
  { label: 'Mais vendidos', sortBy: 'bestseller', sortDirection: 'desc' },
  { label: 'Menor preço', sortBy: 'price', sortDirection: 'asc' },
  { label: 'Maior preço', sortBy: 'price', sortDirection: 'desc' },
  { label: 'Nome A-Z', sortBy: 'name', sortDirection: 'asc' },
  { label: 'Nome Z-A', sortBy: 'name', sortDirection: 'desc' },
];

type CatalogQuery = Omit<StoreCatalogFilter, 'pageNumber'>;

function pageRange(from: number, to: number): number[] {
  return Array.from({ length: Math.max(to - from + 1, 0) }, (_, index) => from + index);
}

function uniqueById(list: StoreCatalogItem[]): StoreCatalogItem[] {
  const seen = new Set<number>();
  return list.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

const sortSelectOptions: SelectOption[] = SORT_OPTIONS.map((opt, idx) => ({ id: idx, label: opt.label }));

function buildTitle(
  search: string,
  categoryIds: number[],
  categories: StoreCategory[],
  gender: number | undefined,
): { title: string; iconIndex: number | undefined } {
  const genderName = genderLabel(gender);
  let base = 'Catálogo';
  let iconIndex: number | undefined;

  if (categoryIds.length === 1) {
    const index = categories.findIndex((c) => c.id === categoryIds[0]);
    if (index >= 0) {
      base = categories[index].name;
      iconIndex = index;
    }
  } else if (categoryIds.length > 1) {
    base = `${categoryIds.length} categorias`;
  } else if (search) {
    base = `Busca por "${search}"`;
  }

  return { title: genderName ? `${base} · ${genderName}` : base, iconIndex };
}

function CloseIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 4h18M6 12h12M10 20h4" />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M7 4v16m0 0l-3-3m3 3l3-3M17 20V4m0 0l-3 3m3-3l3 3" />
    </svg>
  );
}

export function CatalogPage() {
  const [items, setItems] = useState<StoreCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [colors, setColors] = useState<StoreColor[]>([]);
  const [priceMax, setPriceMax] = useState(PRICE_MAX_FALLBACK);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [showSortSheet, setShowSortSheet] = useState(false);
  const headerOffset = useHeaderOffset();

  const filters = useCatalogFilters();
  const previousQueryKeyRef = useRef<string | null>(null);
  const currentQueryRef = useRef<CatalogQuery | null>(null);
  const loadedPagesRef = useRef(0);
  const requestIdRef = useRef(0);
  const fetchingRef = useRef(false);
  const lastTrackedSearchRef = useRef('');

  useInitEffect(() => {
    storeService.getCategories().then((data) => setCategories(sortFeaturedFirst(data))).catch(() => {});
    storeService.getColors().then(setColors).catch(() => {});
    storeService
      .getCatalog({ pageNumber: 1, pageSize: 1, sortBy: 'price', sortDirection: 'desc' })
      .then((data) => {
        const top = data.items[0];
        const highest = top ? Math.max(top.promotionalPrice ?? 0, top.price ?? 0) : 0;
        if (highest > 0) setPriceMax(Math.max(PRICE_STEP, Math.ceil(highest / PRICE_STEP) * PRICE_STEP));
      })
      .catch(() => {});
  });

  const fetchPages = useCallback(async (query: CatalogQuery, from: number, to: number, append: boolean) => {
    const requestId = ++requestIdRef.current;
    fetchingRef.current = true;
    if (append) {
      setLoadingMore(true);
      setLoadMoreFailed(false);
    } else {
      setLoading(true);
      setLoadMoreFailed(false);
    }
    try {
      const pages = await Promise.all(
        pageRange(from, to).map((pageNumber) => storeService.getCatalog({ ...query, pageNumber })),
      );
      if (requestId !== requestIdRef.current) return;
      const fetched = pages.flatMap((page) => page.items);
      setItems((previous) => uniqueById(append ? [...previous, ...fetched] : fetched));
      setTotal(pages[pages.length - 1]?.count ?? 0);
      loadedPagesRef.current = to;
      const term = query.search?.trim() ?? '';
      if (!append && term && term !== lastTrackedSearchRef.current) {
        lastTrackedSearchRef.current = term;
        trackStoreEvent({ type: StoreEventType.Search, searchTerm: term, resultsCount: pages[0]?.count ?? 0 });
      }
    } catch {
      if (requestId !== requestIdRef.current) return;
      if (append) {
        setLoadMoreFailed(true);
      } else {
        setItems([]);
        setTotal(0);
        loadedPagesRef.current = to;
      }
    } finally {
      if (requestId === requestIdRef.current) {
        fetchingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const selectedSort = SORT_OPTIONS[filters.selectedSortIndex];

  useEffect(() => {
    const query: CatalogQuery = {
      pageSize: PAGE_SIZE,
      search: filters.search || undefined,
      categoryIds: filters.categoryIds.length > 0 ? filters.categoryIds : undefined,
      collectionId: filters.collectionId,
      colorId: filters.colorId,
      variationTypeId: filters.variationTypeId,
      variationValues: filters.variationValues.length > 0 ? filters.variationValues : undefined,
      gender: filters.gender,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      hasPromotion: filters.hasPromotion || undefined,
      sortBy: selectedSort.sortBy,
      sortDirection: selectedSort.sortDirection,
    };

    const queryKey = JSON.stringify(query);
    const requestedPages = Math.max(filters.page, 1);

    if (queryKey !== previousQueryKeyRef.current) {
      previousQueryKeyRef.current = queryKey;
      currentQueryRef.current = query;
      loadedPagesRef.current = 0;
      fetchPages(query, 1, requestedPages, false);
    } else if (requestedPages > loadedPagesRef.current && !fetchingRef.current) {
      fetchPages(query, loadedPagesRef.current + 1, requestedPages, true);
    }
  }, [filters.page, filters.search, filters.categoryIds, filters.collectionId, filters.colorId, filters.variationTypeId, filters.variationValues, filters.gender, filters.minPrice, filters.maxPrice, filters.hasPromotion, selectedSort, fetchPages]);

  const hasMore = !loading && items.length < total;
  const shownPercent = total > 0 ? Math.min(100, Math.round((items.length / total) * 100)) : 0;

  const handleLoadMore = () => {
    if (loadingMore) return;
    if (loadMoreFailed && currentQueryRef.current && filters.page > loadedPagesRef.current) {
      fetchPages(currentQueryRef.current, loadedPagesRef.current + 1, filters.page, true);
      return;
    }
    filters.setPage(loadedPagesRef.current + 1);
  };


  const activeFiltersCount = [
    filters.gender !== undefined,
    filters.hasPromotion,
    filters.colorId !== undefined,
    filters.minPriceInput !== '',
    filters.maxPriceInput !== '',
    filters.categoryIds.length > 0,
    filters.variationValues.length > 0,
  ].filter(Boolean).length;

  const { title, iconIndex } = buildTitle(filters.search, filters.categoryIds, categories, filters.gender);
  const countLabel = loading ? 'Buscando...' : `${total} ${total === 1 ? 'produto' : 'produtos'}`;
  const description =
    filters.categoryIds.length === 1 ? categories.find((c) => c.id === filters.categoryIds[0])?.description?.trim() : undefined;

  const showClearInSheet = filters.hasFilters;
  const searchTermChip = filters.search ? (
    <button
      type="button"
      onClick={() => filters.handleSearchChange('')}
      aria-label={`Remover busca ${filters.search}`}
      className="shrink-0 inline-flex items-center gap-1.5 min-h-[44px] pl-3 pr-3 rounded-full bg-champagne border border-champagne-dark text-sm text-text-dark whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate"
    >
      Busca: {filters.search}
      <CloseIcon />
    </button>
  ) : null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 pt-3 pb-8 md:pt-4 md:pb-10">
      <GenderAccent gender={filters.gender} />
      <header className="mb-3 sm:mb-5 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl bg-champagne/70 border border-champagne-dark px-4 py-3 sm:py-3.5 sm:px-5">
        <span className="hidden sm:flex w-12 h-12 shrink-0 rounded-2xl bg-white items-center justify-center">
          <img src={categoryIconSrc(iconIndex ?? 6)} alt="" className="w-9 h-9 object-contain" />
        </span>
        <div className="min-w-0 flex-1 basis-48">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-text-dark text-balance leading-tight">{title}</h1>
          {description && <p className="text-sm text-text-medium mt-0.5 max-w-2xl line-clamp-2 sm:line-clamp-none">{description}</p>}
        </div>
        <p
          className="hidden sm:block shrink-0 px-4 py-1.5 rounded-full bg-white border border-champagne-dark text-sm font-semibold text-text-dark"
          aria-live="polite"
        >
          {countLabel}
        </p>
      </header>

      <div
        className="lg:hidden sticky z-sticky-cta -mx-4 px-4 py-2 bg-white/95 backdrop-blur border-b border-champagne flex items-center gap-2"
        style={{ top: headerOffset }}
      >
        <button
          type="button"
          onClick={() => setShowFilterSheet(true)}
          aria-haspopup="dialog"
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-chocolate text-sm font-semibold text-chocolate-dark hover:bg-champagne-light transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate"
        >
          <FilterIcon />
          Filtrar
          {activeFiltersCount > 0 && (
            <span
              className="bg-chocolate-dark text-white text-xs min-w-5 h-5 px-1 rounded-full flex items-center justify-center font-semibold"
              aria-label={`${activeFiltersCount} ${activeFiltersCount === 1 ? 'filtro ativo' : 'filtros ativos'}`}
            >
              {activeFiltersCount}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setShowSortSheet(true)}
          aria-haspopup="dialog"
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-champagne-dark text-sm font-semibold text-text-dark hover:border-chocolate transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate"
        >
          <SortIcon />
          Ordenar
        </button>
        <p className="ml-auto text-xs text-text-medium whitespace-nowrap" aria-live="polite">
          {countLabel}
        </p>
      </div>

      <div className="lg:hidden -mx-4 px-4 py-3 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-2 w-max pr-4">
          {searchTermChip}
          <BabySizeChips filters={filters} layout="inline" />
          <AgeBucketChips filters={filters} />
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-6 lg:items-start">
        <aside className="hidden lg:block">
          <div className="bg-white rounded-2xl shadow-sm p-4 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-y-contain">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-text-dark text-base">Filtros</h2>
              {filters.hasFilters && (
                <button
                  type="button"
                  onClick={filters.clearFilters}
                  className="text-sm text-chocolate-dark font-medium hover:text-chocolate transition-colors"
                >
                  Limpar
                </button>
              )}
            </div>
            <CatalogFilterPanel colors={colors} filters={filters} priceMax={priceMax} />
          </div>
        </aside>

        <div className="min-w-0">
          <div className="hidden lg:block bg-white rounded-2xl shadow-sm p-4 mb-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar por nome ou descrição..."
                  aria-label="Buscar produtos"
                  value={filters.searchInput}
                  onChange={(e) => filters.handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-9 py-2.5 rounded-full border border-champagne-dark text-text-dark placeholder-text-light bg-champagne-light/40 focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate focus:bg-white transition-colors text-sm"
                />
                {filters.searchInput && (
                  <button
                    type="button"
                    aria-label="Limpar busca"
                    onClick={() => filters.handleSearchChange('')}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-light hover:text-chocolate transition-colors"
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>

              {filters.hasFilters && (
                <button
                  type="button"
                  onClick={filters.clearFilters}
                  className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full border border-chocolate text-sm font-semibold text-chocolate-dark hover:bg-champagne-light transition-colors whitespace-nowrap"
                >
                  <CloseIcon />
                  Limpar filtros
                </button>
              )}

              <div className="w-full sm:w-48">
                <SearchableSelect
                  options={sortSelectOptions}
                  value={filters.selectedSortIndex}
                  onChange={(idx) => filters.handleSortChange(idx ?? 0)}
                  required
                  className="text-xs"
                />
              </div>
            </div>

            <CategorySuggestions categories={categories} filters={filters} />
          </div>

          <div className="hidden lg:block">
            <CategoryStrip categories={categories} filters={filters} />
            <BabySizeChips filters={filters} />
          </div>

          {(loading || items.length > 0) && (
            <ActiveFilters colors={colors} filters={filters} className="hidden lg:flex mb-4" />
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="Nenhuma peça com esses filtros"
              description={filters.hasFilters ? 'Remova algum filtro abaixo para ver mais peças.' : 'Nenhum produto disponível no momento.'}
              action={
                filters.hasFilters && (
                  <div className="flex flex-col items-center gap-4">
                    <ActiveFilters colors={colors} filters={filters} className="justify-center" />
                    <button
                      type="button"
                      onClick={filters.clearFilters}
                      className="min-h-[44px] px-5 py-2.5 rounded-full bg-chocolate-dark hover:bg-chocolate-deep text-white text-sm font-semibold transition-colors"
                    >
                      Ver todo o catálogo
                    </button>
                  </div>
                )
              }
            />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    colorId={filters.colorId}
                    variationValue={filters.variationValues[0]}
                  />
                ))}
              </div>

              <div className="mt-10 flex flex-col items-center gap-3">
                <p className="text-sm text-text-medium" aria-live="polite">
                  Mostrando {items.length} de {total} {total === 1 ? 'produto' : 'produtos'}
                </p>
                <div className="h-1 w-48 overflow-hidden rounded-full bg-champagne" aria-hidden="true">
                  <div className="h-full rounded-full bg-chocolate-dark transition-[width] duration-300" style={{ width: `${shownPercent}%` }} />
                </div>
                {loadMoreFailed && (
                  <p className="text-sm text-danger" role="alert">
                    Não conseguimos carregar mais produtos. Tente de novo.
                  </p>
                )}
                {hasMore && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleLoadMore}
                    loading={loadingMore}
                    className="mt-1 min-h-[48px] w-full max-w-xs rounded-full font-semibold"
                  >
                    {loadMoreFailed ? 'Tentar de novo' : loadingMore ? 'Carregando...' : 'Ver mais produtos'}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <CatalogSheet
        open={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        title="Filtros"
        closeLabel="Fechar filtros"
        headerAction={
          showClearInSheet && (
            <button
              type="button"
              onClick={filters.clearFilters}
              className="min-h-[44px] px-3 text-sm text-chocolate-dark font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate rounded-lg"
            >
              Limpar
            </button>
          )
        }
        footer={
          <button
            type="button"
            onClick={() => setShowFilterSheet(false)}
            className="w-full min-h-[44px] bg-chocolate-dark hover:bg-chocolate-deep text-white text-sm font-semibold py-3 rounded-lg transition-colors"
          >
            {total === 1 ? 'Ver 1 produto' : `Ver ${total} produtos`}
          </button>
        }
      >
        <CatalogFilterPanel colors={colors} filters={filters} priceMax={priceMax} categories={categories} />
      </CatalogSheet>

      <CatalogSheet
        open={showSortSheet}
        onClose={() => setShowSortSheet(false)}
        title="Ordenar por"
        closeLabel="Fechar ordenação"
      >
        <ul role="radiogroup" aria-label="Ordenar por" className="flex flex-col">
          {SORT_OPTIONS.map((option, idx) => {
            const selected = filters.selectedSortIndex === idx;
            return (
              <li key={option.label}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    filters.handleSortChange(idx);
                    setShowSortSheet(false);
                  }}
                  className={`w-full flex items-center justify-between gap-3 min-h-[48px] px-2 rounded-lg text-left text-base transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-chocolate ${
                    selected ? 'font-semibold text-chocolate-dark bg-champagne/60' : 'text-text-dark hover:bg-champagne-light'
                  }`}
                >
                  {option.label}
                  {selected && (
                    <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </CatalogSheet>
    </div>
  );
}
